import {OrgFeatureDto} from "./org-feature.dto";
import {Feature} from "../../libs/enum/feature.enum";
import {isDefined, isFalse, isTrue, notDefined} from "../../libs/utils/epic.utils";
import {FeaturePriceDto} from "./feature-price.dto";

export class FeatureDto {

  public readonly id!: string;
  public readonly type!: Feature;
  public readonly multiActivatable!: boolean;
  public readonly activatable: boolean | undefined;
  public readonly cancellable: boolean | undefined;
  public readonly autoAdd!: boolean;
  public readonly free!: boolean;
  public readonly trial!: boolean;
  public readonly pos!: number;
  public readonly prices: FeaturePriceDto[] | [];
  public readonly orgFeatures: OrgFeatureDto[] = [];

  constructor(init?: Partial<FeatureDto> | undefined) {
    Object.assign(this, init);
    if (isDefined(init?.prices)) {
      this.prices = init.prices.map((i) => new FeaturePriceDto(i)).sort((a, b) => {
        if (a.paymentPeriod < b.paymentPeriod) {
          return -1;
        }
        if (a.paymentPeriod > b.paymentPeriod) {
          return 1;
        }
        return 0;
      });
    } else {
      this.prices = [];
    }
    if (isDefined(init?.orgFeatures)) {
      this.orgFeatures = init.orgFeatures.map((i) => new OrgFeatureDto(i)).sort((i1, i2) => {
        if (notDefined(i1.validFrom)) {
          return 1;
        }
        if (notDefined(i2.validFrom)) {
          return -1;
        }
        return i1.validFrom.getTime() - i2.validFrom.getTime();
      });
    } else {
      this.orgFeatures = [];
    }
  }

  public getNameMessageCode(): string {
    return 'feature.' + this.type.toLowerCase();
  }

  // noinspection JSUnusedGlobalSymbols // Used in Table-Template
  public getInfoMessageCode(): string {
    let code: string = this.getNameMessageCode();
    if (this.isTrial()) {
      code = code.replace('trial_', '');
    }
    return code + '.info';
  }

  // noinspection JSUnusedGlobalSymbols // Used in Table-Template
  public getHintMessageCode(): string {
    let code: string = this.getNameMessageCode();
    if (this.isTrial()) {
      code = code.replace('trial_', '');
    }
    return code + '.hint';
  }

  // noinspection JSUnusedGlobalSymbols // Used in Table-Template
  public hasOrgFeatures(): boolean {
    return isDefined(this.orgFeatures) && this.orgFeatures.length > 0;
  }

  // noinspection JSUnusedGlobalSymbols // Used in Table-Template
  public hasPrices(): boolean {
    return this.prices && this.prices.length > 0;
  }

  // noinspection JSUnusedGlobalSymbols // Used in Table-Template
  public isDisabled(): boolean {
    return isTrue(this.autoAdd);
  }

  public isFree(): boolean {
    return isTrue(this.free);
  }

  public isTrial(): boolean {
    return isTrue(this.trial);
  }

  public notTrial(): boolean {
    return !this.isTrial();
  }

  public isActivatable(): boolean {
    return isTrue(this.activatable);
  }

  public isCancellable(): boolean {
    return isTrue(this.cancellable);
  }

  public notCancellable(): boolean {
    return !this.isCancellable();
  }

  // Feature already purchased
  public isPaid(): boolean {
    return this.hasOrgFeatures();
  }

  // Feature can be activated for free
  // noinspection JSUnusedGlobalSymbols // Used in Table-Template
  public allowFreeActivation(): boolean {
    const free: boolean = this.isActivatable() && this.notCancellable() && !this.isForSale();
    if (free && this.isTrial()) {
      return notDefined(this.orgFeatures) || this.orgFeatures.length === 0;
    }
    return free;
  }

  // Feature can be bought
  // noinspection JSUnusedGlobalSymbols // Used in Table-Template
  public allowBuy(): boolean {
    return this.isActivatable() && this.notCancellable() && this.isForSale() && this.hasPrices();
  }

  public isForSale(): boolean {
    return isDefined(this.prices)
      && isFalse(this.isTrial())
      && isFalse(this.isFree())
      && (isFalse(this.isPaid()) || this.multiActivatable);
  }

  // noinspection JSUnusedGlobalSymbols // Used in Table-Template
  public getFirstPrice(): FeaturePriceDto | undefined {
    if (this.prices && this.prices.length > 0) {
      return this.prices[0];
    }
    return undefined;
  }

  // noinspection JSUnusedGlobalSymbols
  public isEmail(): boolean {
    return this.type === Feature.EMAIL_SEND_BASIC
      || this.type === Feature.EMAIL_SEND_ONCE
      || this.type === Feature.EMAIL_SEND_SUBS_SMALL
      || this.type === Feature.EMAIL_SEND_SUBS_MEDIUM;
  }

  // noinspection JSUnusedGlobalSymbols
  public isStorage(): boolean {
    return this.type === Feature.STORAGE_BASIC
      || this.type === Feature.STORAGE_SMALL
      || this.type === Feature.STORAGE_MEDIUM
      || this.type === Feature.STORAGE_LARGE;
  }

  public isWalletTopUp(): boolean {
    return this.type === Feature.WALLET_TOP_UP_SMALL
      || this.type === Feature.WALLET_TOP_UP_MEDIUM
      || this.type === Feature.WALLET_TOP_UP_LARGE;
  }
}
